/**
 * Checks if a token is expired by comparing the expiration date (in UTC)
 * with the current UTC time
 *
 * @param expiresAt ISO string date in UTC
 * @returns boolean - true if token is expired or invalid
 */
export const isTokenExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return true;

  try {
    // Parse the expiration date from the string
    const expDate = new Date(expiresAt);

    // Get current time in UTC
    const nowUtc = new Date();

    // Compare dates
    return expDate <= nowUtc;
  } catch (error) {
    console.error("Error parsing token expiration date:", error);
    return true; // If there's any error, consider the token expired
  }
};

export type PersistedAuth = {
  token: string | null;
  expiresAt: string | null;
};

export const getPersistedAuthSync = (): PersistedAuth => {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return { token: null, expiresAt: null };
    const parsed = JSON.parse(raw);
    const st = parsed?.state ?? {};
    return {
      token: (st.token ?? null) as string | null,
      expiresAt: (st.expiresAt ?? null) as string | null,
    };
  } catch {
    return { token: null, expiresAt: null };
  }
};

export const getEffectiveAuth = (
  storeAuth: PersistedAuth
): { token: string | null; expiresAt: string | null; tokenValid: boolean } => {
  const persisted = getPersistedAuthSync();
  const token = storeAuth.token ?? persisted.token;
  const expiresAt = storeAuth.expiresAt ?? persisted.expiresAt;
  const tokenValid = !!token && !isTokenExpired(expiresAt);
  return { token, expiresAt, tokenValid };
};
